import React, { useState, useEffect } from 'react';
import { FileText, Plus, Edit2, Trash2, Search, X, RefreshCw, CheckCircle } from 'lucide-react';
import { API_URL } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const TIMEZONE_CONFIG = {
  name: 'Asia/Kolkata',
  offset: 5.5, // UTC offset in hours
  locale: 'en-IN',
  formatOptions: {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }
};

const TIMESTAMP_NORMALIZATION = {
  // Patterns to detect different timestamp formats
  patterns: {
    hasTimezone: /[Z+\-]\d{2}:?\d{2}?$/,
    hasISOFormat: /T/,
    postgresFormat: /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/
  },
  // Normalization rules
  rules: {
    postgresSpaceReplacement: 'T',
    utcSuffix: 'Z'
  }
};

const UI_CONFIG = {
  header: {
    title: 'Ads.txt Verification Dashboard',
    subtitle: 'Verify ads.txt entries against live domain endpoints',
    icon: {
      gradient: 'from-orange-500 to-red-600'
    }
  },
  buttons: {
    addDomain: {
      label: 'Add Domain',
      bgColor: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600'
    }
  },
  modals: {
    add: {
      title: 'Add New Domain',
      submitLabel: 'Add Domain',
      cancelLabel: 'Cancel'
    },
    edit: {
      titleNew: 'Set Expected Ads.txt Content',
      titleEdit: 'Edit Expected Ads.txt Content',
      submitLabelNew: 'Save Content',
      submitLabelEdit: 'Update Content',
      cancelLabel: 'Cancel'
    },
    delete: {
      title: 'Delete Ads.txt Entry',
      message: 'Are you sure you want to delete the ads.txt entry for',
      submitLabel: 'Delete',
      cancelLabel: 'Cancel',
      submitColor: 'bg-red-500 hover:bg-red-600'
    }
  },
  search: {
    placeholder: 'Search by domain...'
  },
  table: {
    emptyMessage: 'No entries found',
    loadingMessage: 'Loading...'
  }
};

const VERIFICATION_RESULT_CONFIG = {
  verified: {
    bgColor: 'bg-green-500/20',
    textColor: 'text-green-400',
    buttonColor: 'bg-blue-500 hover:bg-blue-600'
  },
  mismatch: {
    bgColor: 'bg-red-500/20',
    textColor: 'text-red-400',
    buttonColor: 'bg-blue-500 hover:bg-blue-600'
  },
  unavailable: {
    bgColor: 'bg-yellow-500/20',
    textColor: 'text-yellow-400',
    buttonColor: 'bg-blue-500 hover:bg-blue-600'
  },
  error: {
    bgColor: 'bg-gray-500/20',
    textColor: 'text-gray-400',
    buttonColor: 'bg-blue-500 hover:bg-blue-600'
  }
};

const STATUS_OPTIONS = [
  { value: 'verified', label: 'Verified', color: 'bg-green-500/20 text-green-400', icon: '✅' },
  { value: 'mismatch', label: 'Mismatch', color: 'bg-red-500/20 text-red-400', icon: '❌' },
  { value: 'unavailable', label: 'Unavailable', color: 'bg-yellow-500/20 text-yellow-400', icon: '⚠️' },
  { value: 'pending', label: 'Pending', color: 'bg-gray-500/20 text-gray-400', icon: '⏳' }
];

const TABLE_HEADERS = [
  { key: 'domain', label: 'Domain' },
  { key: 'status', label: 'Verification Status' },
  { key: 'last_checked', label: 'Last Verified' },
  { key: 'actions', label: 'Actions', align: 'right' }
];

const FORM_CONFIG = {
  fields: {
    domain: {
      label: 'Domain',
      type: 'text',
      placeholder: 'example.com',
      required: true,
      readonly: true
    },
    content: {
      label: 'Expected Ads.txt Content',
      type: 'textarea',
      placeholder: 'google.com, pub-1203149545224208, DIRECT, f08c47fec0942fa0\ngoogle.com, pub-2730263451308801, DIRECT, f08c47fec0942fa0',
      rows: 6,
      required: true,
      helperText: 'Enter the expected ads.txt entries. The system will verify against https://domain/ads.txt'
    },
    check_interval: {
      label: 'Check Interval (minutes)',
      type: 'number',
      placeholder: '1440',
      min: 1,
      required: false,
      helperText: 'How often to check this certificate (in minutes). Default: 1440 (24 hours)'
    }
  },
  fieldOrder: ['domain', 'content', 'check_interval']
};

const FORM_FIELDS = FORM_CONFIG.fieldOrder.map(name => ({
  name,
  ...FORM_CONFIG.fields[name]
}));

const INITIAL_FORM_STATE = {
  domain: '',
  content: '',
  check_interval: 1440
};

function AdsTxtManager() {
  const { getAuthHeaders } = useAuth();
  const [adsTxtEntries, setAdsTxtEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentEntry, setCurrentEntry] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [verificationResult, setVerificationResult] = useState(null);

  // Get status icon and color dynamically
  const getStatusDisplay = (status) => {
    const statusOption = STATUS_OPTIONS.find(opt => opt.value === status);
    if (!statusOption) return { color: 'bg-gray-500/20 text-gray-400', icon: '❓', label: status };
    return statusOption;
  };

  // Get verification result styling
  const getVerificationResultStyle = (type) => {
    return VERIFICATION_RESULT_CONFIG[type] || VERIFICATION_RESULT_CONFIG.error;
  };

  // Normalize timestamp to UTC format dynamically
  const normalizeTimestampToUTC = (dateString) => {
    if (!dateString) return null;
    
    const { patterns, rules } = TIMESTAMP_NORMALIZATION;
    let normalizedString = dateString;
    
    // Already has timezone info - use as-is
    if (patterns.hasTimezone.test(dateString)) {
      return normalizedString;
    }
    
    // PostgreSQL format: 'YYYY-MM-DD HH:MM:SS' -> 'YYYY-MM-DDTHH:MM:SSZ'
    if (patterns.postgresFormat.test(dateString) && !patterns.hasISOFormat.test(dateString)) {
      normalizedString = dateString.replace(' ', rules.postgresSpaceReplacement) + rules.utcSuffix;
    }
    // ISO format without timezone: 'YYYY-MM-DDTHH:MM:SS' -> 'YYYY-MM-DDTHH:MM:SSZ'
    else if (patterns.hasISOFormat.test(dateString)) {
      normalizedString = dateString + rules.utcSuffix;
    }
    
    return normalizedString;
  };

  // Format date dynamically based on configured timezone
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    
    // Normalize to UTC format
    const utcString = normalizeTimestampToUTC(dateString);
    if (!utcString) return 'Invalid Date';
    
    const date = new Date(utcString);
    
    // Validate date
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    // Apply timezone configuration
    return date.toLocaleString(TIMEZONE_CONFIG.locale, {
      timeZone: TIMEZONE_CONFIG.name,
      ...TIMEZONE_CONFIG.formatOptions
    });
  };

  // Fetch all ads.txt entries
  // Fetch all ads.txt entries
  const fetchAdsTxt = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/ads-txt`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAdsTxtEntries(data);
      }
    } catch (error) {
      console.error('Error fetching ads.txt entries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdsTxt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open add modal for new domain
  const openAddModal = () => {
    setCurrentEntry(null);
    setFormData(INITIAL_FORM_STATE);
    setShowAddModal(true);
  };

  // Open edit modal
  const openEditModal = (entry) => {
    setCurrentEntry(entry);
    setFormData({
      domain: entry.domain,
      content: entry.content || '',
      status: entry.status || 'pending',
      check_interval: entry.check_interval || 1440
    });
    setShowEditModal(true);
  };

  // Add new entry
  const handleAdd = async () => {
    // Validate domain
    if (!formData.domain || !formData.domain.trim()) {
      setVerificationResult({
        show: true,
        type: 'error',
        title: 'Validation Error',
        message: 'Domain name is required.'
      });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/ads-txt`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        fetchAdsTxt();
        setShowAddModal(false);
        setFormData(INITIAL_FORM_STATE);
      } else {
        const error = await response.json();
        setVerificationResult({
          show: true,
          type: 'error',
          title: 'Add Failed',
          message: error.error || 'Failed to add domain.'
        });
      }
    } catch (error) {
      console.error('Error adding domain:', error);
      setVerificationResult({
        show: true,
        type: 'error',
        title: 'Add Failed',
        message: 'Failed to add domain. Please try again.'
      });
    }
  };

  // Update entry
  const handleUpdate = async () => {
    try {
      const response = await fetch(`${API_URL}/ads-txt/${currentEntry.id}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        fetchAdsTxt();
        setShowEditModal(false);
        setCurrentEntry(null);
        setFormData(INITIAL_FORM_STATE);
      }
    } catch (error) {
      console.error('Error updating ads.txt entry:', error);
    }
  };

  // Delete entry
  const handleDelete = async () => {
    if (!currentEntry.id) {
      setShowDeleteModal(false);
      setCurrentEntry(null);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/ads-txt/${currentEntry.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        fetchAdsTxt();
        setShowDeleteModal(false);
        setCurrentEntry(null);
      }
    } catch (error) {
      console.error('Error deleting ads.txt entry:', error);
    }
  };

  // Verify ads.txt for a domain
  const handleVerify = async (entry) => {
    try {
      setVerifying(prev => ({ ...prev, [entry.id]: true }));
      const response = await fetch(`${API_URL}/ads-txt/${entry.id}/verify`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        if (result.verification_result) {
          const statusConfig = getStatusDisplay(result.verification_result.status);
          setVerificationResult({
            show: true,
            type: result.verification_result.status,
            title: 'Verification Complete',
            message: result.verification_result.message,
            icon: statusConfig.icon,
            domain: entry.domain
          });
        }
        fetchAdsTxt();
      }
    } catch (error) {
      console.error('Error verifying ads.txt:', error);
      setVerificationResult({
        show: true,
        type: 'error',
        title: 'Verification Failed',
        message: 'Unable to verify ads.txt. Please try again.'
      });
    } finally {
      setVerifying(prev => ({ ...prev, [entry.id]: false }));
    }
  };

  // Open delete modal
  const openDeleteModal = (entry) => {
    setCurrentEntry(entry);
    setShowDeleteModal(true);
  };

  // Filter entries
  const filteredEntries = adsTxtEntries.filter(entry =>
    entry.domain.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 bg-gradient-to-br ${UI_CONFIG.header.icon.gradient} rounded-lg flex items-center justify-center`}>
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">{UI_CONFIG.header.title}</h1>
            <p className="text-sm text-gray-400">{UI_CONFIG.header.subtitle}</p>
          </div>
        </div>
        <button
          onClick={openAddModal}
          className={`flex items-center gap-2 px-4 py-2 ${UI_CONFIG.buttons.addDomain.bgColor} ${UI_CONFIG.buttons.addDomain.hoverColor} rounded-lg transition-colors`}
        >
          <Plus className="w-5 h-5" />
          {UI_CONFIG.buttons.addDomain.label}
        </button>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={UI_CONFIG.search.placeholder}
          className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:border-blue-500 focus:outline-none"
        />
      </div>

      {/* Entries Table */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900">
              <tr>
                {TABLE_HEADERS.map((header) => (
                  <th 
                    key={header.key}
                    className={`px-6 py-3 text-${header.align || 'left'} text-xs font-medium text-gray-400 uppercase tracking-wider`}
                  >
                    {header.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-400">{UI_CONFIG.table.loadingMessage}</td>
                </tr>
              ) : filteredEntries.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-400">{UI_CONFIG.table.emptyMessage}</td>
                </tr>
              ) : (
                filteredEntries.map((entry) => {
                  const statusDisplay = getStatusDisplay(entry.status);
                  return (
                    <tr key={`${entry.domain}-${entry.id || 'new'}`} className="hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium">{entry.domain}</td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs flex items-center gap-1 w-fit ${statusDisplay.color}`}>
                          <span>{statusDisplay.icon}</span>
                          {statusDisplay.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {formatDate(entry.last_checked)}
                      </td>
                      <td className="px-6 py-4 text-sm text-right">
                        <div className="flex items-center justify-end gap-2">
                          {entry.content && (
                            <button
                              onClick={() => handleVerify(entry)}
                              disabled={verifying[entry.id]}
                              className="p-2 hover:bg-gray-600 rounded transition-colors disabled:opacity-50"
                              title="Verify ads.txt"
                            >
                              {verifying[entry.id] ? (
                                <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />
                              ) : (
                                <CheckCircle className="w-4 h-4 text-blue-400" />
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => openEditModal(entry)}
                            className="p-2 hover:bg-gray-600 rounded transition-colors"
                            title="Edit expected content"
                          >
                            <Edit2 className="w-4 h-4 text-blue-400" />
                          </button>
                          <button
                            onClick={() => openDeleteModal(entry)}
                            className="p-2 hover:bg-gray-600 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 max-w-2xl w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h3 className="text-xl font-bold">{UI_CONFIG.modals.edit.titleEdit}</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {FORM_FIELDS.map((field) => (
                <div key={field.name}>
                  <label className="block text-sm font-medium mb-2">{field.label}</label>
                  {field.type === 'textarea' ? (
                    <>
                      <textarea
                        value={formData[field.name]}
                        onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                        placeholder={field.placeholder}
                        rows={field.rows}
                        readOnly={field.readonly}
                        className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg focus:border-blue-500 focus:outline-none font-mono text-sm"
                      />
                      {field.helperText && (
                        <p className="text-xs text-gray-500 mt-1">{field.helperText}</p>
                      )}
                    </>
                  ) : field.type === 'number' ? (
                    <>
                      <input
                        type="number"
                        value={formData[field.name]}
                        onChange={(e) => setFormData({ ...formData, [field.name]: parseInt(e.target.value) || 0 })}
                        placeholder={field.placeholder}
                        min={field.min}
                        className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                      {field.helperText && (
                        <p className="text-xs text-gray-500 mt-1">{field.helperText}</p>
                      )}
                    </>
                  ) : (
                    <>
                      <input
                        type={field.type}
                        value={formData[field.name]}
                        onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                        placeholder={field.placeholder}
                        readOnly={field.readonly}
                        className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                      {field.helperText && (
                        <p className="text-xs text-gray-500 mt-1">{field.helperText}</p>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                {UI_CONFIG.modals.edit.cancelLabel}
              </button>
              <button
                onClick={handleUpdate}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
              >
                {UI_CONFIG.modals.edit.submitLabelEdit}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 max-w-md w-full">
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4">{UI_CONFIG.modals.delete.title}</h3>
              <p className="text-gray-400 mb-6">
                {UI_CONFIG.modals.delete.message} <span className="font-semibold text-white">{currentEntry?.domain}</span>?
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                >
                  {UI_CONFIG.modals.delete.cancelLabel}
                </button>
                <button
                  onClick={handleDelete}
                  className={`px-4 py-2 ${UI_CONFIG.modals.delete.submitColor} rounded-lg transition-colors`}
                >
                  {UI_CONFIG.modals.delete.submitLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Domain Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h3 className="text-xl font-bold">{UI_CONFIG.modals.add.title}</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {FORM_FIELDS.map((field) => (
                <div key={field.name}>
                  <label className="block text-sm font-medium mb-2">{field.label}</label>
                  {field.type === 'textarea' ? (
                    <>
                      <textarea
                        value={formData[field.name]}
                        onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                        placeholder={field.placeholder}
                        rows={field.rows}
                        className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg focus:border-blue-500 focus:outline-none font-mono text-sm"
                      />
                      {field.helperText && (
                        <p className="text-xs text-gray-500 mt-1">{field.helperText}</p>
                      )}
                    </>
                  ) : field.type === 'select' ? (
                    <>
                      <select
                        value={formData[field.name]}
                        onChange={(e) => setFormData({ ...formData, [field.name]: parseInt(e.target.value) })}
                        className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg focus:border-blue-500 focus:outline-none"
                      >
                        {field.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {field.helperText && (
                        <p className="text-xs text-gray-500 mt-1">{field.helperText}</p>
                      )}
                    </>
                  ) : (
                    <>
                      <input
                        type={field.type}
                        value={formData[field.name]}
                        onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                        placeholder={field.placeholder}
                        className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg focus:border-blue-500 focus:outline-none"
                      />
                      {field.helperText && (
                        <p className="text-xs text-gray-500 mt-1">{field.helperText}</p>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                {UI_CONFIG.modals.add.cancelLabel}
              </button>
              <button
                onClick={handleAdd}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
              >
                {UI_CONFIG.modals.add.submitLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Verification Result Modal */}
      {verificationResult?.show && (() => {
        const resultStyle = getVerificationResultStyle(verificationResult.type);
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg border border-gray-700 max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl ${resultStyle.bgColor}`}>
                    {verificationResult.icon || '❓'}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold">{verificationResult.title}</h3>
                    {verificationResult.domain && (
                      <p className="text-sm text-gray-400">{verificationResult.domain}</p>
                    )}
                  </div>
                </div>
                <p className={`mb-6 ${resultStyle.textColor}`}>
                  {verificationResult.message}
                </p>
                <div className="flex justify-end">
                  <button
                    onClick={() => setVerificationResult(null)}
                    className={`px-6 py-2 ${resultStyle.buttonColor} rounded-lg transition-colors font-medium`}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default AdsTxtManager;
